
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { processSteps, faqs } from '../data';

const fadeInUp = {
  hidden: { opacity: 0, y: 50, filter: 'blur(10px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  }
};

const About = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const { scrollY } = useScroll();
  const yHero = useTransform(scrollY, [0, 600], [0, 200]);

  return (
    <main className="font-geist text-primary bg-white min-h-screen overflow-x-hidden">

      {/* Hero Section */}
      <section className="relative h-[80vh] flex items-center overflow-hidden bg-primary">
        <motion.div
          className="absolute inset-0 z-0"
          style={{ y: yHero }}
        >
          <img
            src="https://ik.imagekit.io/6dlukcyok/Medical%20bucket/pexels-mart-production-7088841.jpg"
            className="w-full h-full object-cover brightness-[0.9] scale-105"
            alt="Medical Team"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/50 to-primary/10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent"></div>
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full">
          <motion.div
            className="max-w-4xl text-left pt-24 md:pt-32"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
            }}
          >
            <motion.div variants={fadeInUp} className="hidden md:flex items-center gap-4 mb-8">
              <span className="h-0.5 w-16 bg-accent"></span>
              <span className="text-accent text-xs font-bold uppercase tracking-[0.5em]">Medical Practice</span>
            </motion.div>

            <motion.h1 variants={fadeInUp} className="text-4xl md:text-6xl lg:text-7xl font-sans-jakarta font-extrabold text-white mb-10 leading-[1.1] tracking-tight drop-shadow-lg">
              A Personalized Path <br />
              to Total <span className="text-teal-400 italic">Wellness</span>.
            </motion.h1>

            <motion.p variants={fadeInUp} className="text-slate-100 text-base md:text-lg font-light max-w-xl leading-relaxed border-l border-white/30 pl-8 drop-shadow-md">
              Dr CG Du Plessis & Partners provides private medical care in Fourways, focusing on long-term relationships and unhurried diagnostic excellence.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* 
          OVERLAPPING CARD SECTION (The Methodology)
      */}
      <section className="py-24 md:py-32 px-6 md:px-8 bg-[#F0FDFB] relative">
        <div className="max-w-7xl mx-auto">

          <motion.div
            className="text-center mb-16 md:mb-24 relative z-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="text-teal-600 font-bold text-xs uppercase tracking-[0.6em] mb-4 block">Our Methodology</span>
            <h2 className="font-sans-jakarta font-extrabold text-3xl md:text-6xl text-primary tracking-tight leading-tight">
              A Seamless <span className="text-teal-600 italic">Patient</span> Journey.
            </h2>
          </motion.div>

          <div className="stack-container flex flex-col gap-8 md:gap-10 max-w-[1000px] mx-auto relative">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.id}
                className="card sticky w-full h-auto min-h-[520px] md:h-[450px] bg-white rounded-[24px] shadow-[0_10px_30px_rgba(0,0,0,0.05)] flex overflow-hidden border border-slate-100"
                style={{
                  top: `${80 + index * 20}px`,
                  zIndex: index + 1
                }}
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {/* Left Side: Image Holder */}
                <div className="hidden md:block w-[45%] h-full bg-slate-50 p-8 border-r border-slate-50">
                  <div className="w-full h-full rounded-2xl overflow-hidden shadow-sm">
                    <img
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Right Side: Content */}
                <div className="flex-1 flex flex-col justify-center p-8 md:p-14 relative">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#1344fe] rounded-2xl flex items-center justify-center text-white mb-6 md:mb-8 shadow-xl shadow-blue-600/20">
                    <span className="material-symbols-outlined text-3xl md:text-4xl font-light">{step.icon}</span>
                  </div>
                  <h3 className="text-2xl md:text-4xl font-sans-jakarta font-extrabold text-primary mb-4 md:mb-6 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-slate-500 font-light text-base md:text-lg leading-relaxed mb-6 md:mb-10 max-w-md">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap gap-6 md:gap-8 pt-6 md:pt-8 border-t border-slate-50">
                    {step.stats.map((stat, sIdx) => (
                      <div key={sIdx} className="flex flex-col">
                        <span className="text-primary font-sans-jakarta font-extrabold text-xl md:text-2xl tracking-tighter">
                          {stat.value}
                        </span>
                        <span className="text-slate-400 text-[8px] md:text-[9px] font-bold uppercase tracking-[0.2em]">
                          {stat.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="h-[15vh] md:h-[20vh]"></div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 px-6 md:px-8 bg-white border-t border-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              className="space-y-16"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
              }}
            >
              <motion.div variants={fadeInUp} className="relative group">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 border border-teal-100">
                    <span className="material-symbols-outlined text-2xl">rocket_launch</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-sans-jakarta font-extrabold tracking-tight text-primary">Our Mission</h3>
                </div>
                <p className="text-lg text-slate-600 font-light leading-relaxed max-w-lg border-l-4 border-teal-500/20 pl-6">
                  To provide compassionate, high-quality medical expertise that empowers our patients through informed decisions.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="relative group">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center text-primary border border-slate-100">
                    <span className="material-symbols-outlined text-2xl">visibility</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-sans-jakarta font-extrabold tracking-tight text-primary">Our Vision</h3>
                </div>
                <p className="text-lg text-slate-600 font-light leading-relaxed max-w-lg border-l-4 border-primary/10 pl-6">
                  To be the gold standard in community general practice, where every patient is recognized as a person.
                </p>
              </motion.div>
            </motion.div>

            <motion.div
              className="relative pl-6"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="aspect-[4/5] rounded-[2.5rem] overflow-hidden shadow-xl rotate-1">
                <img
                  src="https://ik.imagekit.io/6dlukcyok/Medical%20bucket/Lochi.avif"
                  className="w-full h-full object-cover"
                  alt="Commitment to Care"
                />
              </div>
              <div className="absolute -bottom-4 -left-2 bg-primary p-8 rounded-2xl shadow-xl text-white">
                <p className="text-teal-400 font-bold text-5xl font-sans-jakarta mb-1 leading-none">100%</p>
                <p className="text-[9px] uppercase tracking-[0.3em] font-bold text-slate-400">Patient Centered Care</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-24 px-6 md:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <span className="text-accent font-bold text-xs uppercase tracking-[0.3em] mb-4 block">Common Questions</span>
            <h2 className="font-sans-jakarta font-extrabold text-3xl md:text-5xl text-primary tracking-tight">Expertly <span className="text-teal-500 italic">Resolved</span>.</h2>
          </motion.div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <motion.div
                key={idx}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 transition-all duration-500 hover:shadow-md group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left focus:outline-none"
                >
                  <span className={`text-lg md:text-xl font-sans-jakarta font-bold transition-colors duration-300 ${openFaq === idx ? 'text-accent' : 'text-primary'}`}>
                    {faq.question}
                  </span>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${openFaq === idx ? 'bg-primary text-white rotate-180' : 'bg-slate-50 text-slate-400'}`}>
                    <span className="material-symbols-outlined text-xl">expand_more</span>
                  </div>
                </button>
                <div className={`px-8 transition-all duration-500 ease-in-out overflow-hidden ${openFaq === idx ? 'max-h-[300px] pb-8 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="h-px w-full bg-slate-50 mb-6"></div>
                  <p className="text-slate-500 leading-relaxed font-light text-base">
                    {faq.answer}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Map Section */}
      <section className="py-24 px-6 md:px-8 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* Contact Details */}
            <motion.div
              className="order-2 lg:order-1"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="text-teal-600 font-bold text-xs uppercase tracking-[0.6em] mb-4 block">Visit Us</span>
              <h2 className="font-sans-jakarta font-extrabold text-3xl md:text-5xl text-primary tracking-tight mb-10">
                Conveniently located in <span className="text-teal-600 italic">Fourways</span>.
              </h2>

              <div className="space-y-8">
                <div className="flex gap-6 group">
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-white transition-colors duration-300 shrink-0">
                    <span className="material-symbols-outlined text-2xl">location_on</span>
                  </div>
                  <div>
                    <h4 className="font-sans-jakarta font-bold text-lg text-primary mb-2">Physical Address</h4>
                    <p className="text-slate-600 font-light leading-relaxed">
                      39 Kingfisher Drive,<br />
                      Kingfisher Shopping Centre,<br />
                      Fourways, Sandton
                    </p>
                  </div>
                </div>

                <div className="flex gap-6 group">
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-white transition-colors duration-300 shrink-0">
                    <span className="material-symbols-outlined text-2xl">schedule</span>
                  </div>
                  <div>
                    <h4 className="font-sans-jakarta font-bold text-lg text-primary mb-2">Opening Hours</h4>
                    <p className="text-slate-600 font-light leading-relaxed">
                      <span className="font-medium text-primary">Monday - Friday:</span> 8:00 am – 5:00 pm<br />
                      <span className="font-medium text-primary">Saturday:</span> 9:00 am – 1:00 pm<br />
                      <span className="font-medium text-primary">Sundays & Public Holidays:</span> Closed
                    </p>
                  </div>
                </div>

                <div className="flex gap-6 group">
                  <div className="w-14 h-14 rounded-2xl bg-slate-50 flex items-center justify-center text-primary group-hover:bg-accent group-hover:text-white transition-colors duration-300 shrink-0">
                    <span className="material-symbols-outlined text-2xl">call</span>
                  </div>
                  <div>
                    <h4 className="font-sans-jakarta font-bold text-lg text-primary mb-2">Get in Touch</h4>
                    <p className="text-slate-600 font-light leading-relaxed flex flex-col gap-1">
                      <a href="tel:0117052197" className="hover:text-accent transition-colors w-fit">011 705 2197</a>
                      <a href="mailto:fourwaysdoctor@gmail.com" className="hover:text-accent transition-colors w-fit">fourwaysdoctor@gmail.com</a>
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div
              className="order-1 lg:order-2 relative h-[450px] w-full rounded-[2.5rem] overflow-hidden shadow-2xl border border-slate-200 group"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="absolute inset-0 z-10 pointer-events-none rounded-[2.5rem] ring-1 ring-inset ring-black/5"></div>
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3585.1730245453896!2d27.99937787572739!3d-26.02789417718567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1e9576a4068f79b3%3A0xa3729c33e5ceef86!2sDr%20CG%20Du%20Plessis%20and%20Partners!5e0!3m2!1sen!2sza!4v1771325136703!5m2!1sen!2sza"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen={true}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="grayscale group-hover:grayscale-0 transition-all duration-1000 ease-in-out"
                title="Practice Location"
              ></iframe>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Footer CTA - Scaled down to match Home/Services */}
      <section id="contact" className="py-20 px-6 md:px-8 bg-white relative scroll-mt-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="relative overflow-hidden bg-primary rounded-[2.5rem] p-10 md:p-14 lg:py-16 text-white shadow-xl text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
              <div className="absolute top-[-30%] left-[-15%] w-[80%] aspect-square bg-accent rounded-full blur-[2px] opacity-100"></div>
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-tr from-primary/40 to-transparent"></div>
            </div>

            <div className="relative z-10 flex flex-col items-center max-w-4xl mx-auto">
              <span className="text-[10px] font-bold uppercase tracking-[0.4em] mb-5 opacity-80">Book Now</span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-sans-jakarta font-extrabold mb-10 leading-[1.1] tracking-tight">
                Ready to experience better health?
              </h2>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
                <a
                  href="https://www.recomed.co.za/by-name/dr%20cg%20du%20plessis%20partners/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 bg-white px-8 py-4 rounded-full text-primary font-bold text-xs tracking-[0.15em] uppercase hover:bg-teal-500 hover:text-white transition-all shadow-lg transform hover:-translate-y-1"
                >
                  <span className="material-symbols-outlined text-lg group-hover:scale-110 transition-transform">calendar_month</span>
                  Book an appointment
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

    </main>
  );
};

export default About;
